/**
 * The brains of the UI. Interfaces with the state manager, emits events for the UI to act on, and receives messages from the logAdapter (via the actions object).
 */
import { RouterClient } from "../../../../clients/routerClient";
import * as Utils from "../../../../common/util";
import { FormattedLogMessage, formatLogMessage } from "../../FormattedLogMessage";
import { CtrlFSearch } from "./CtrlFSearch";
import ConfigClient from "../../../../clients/configClient";
import { System } from "../../../../common/system";
import HotkeyClient from "../../../../clients/hotkeyClient";
import SystemManagerClient from "../../../../common/systemManagerClient";
import { toast } from "../../../../common/toast";
import {
	CentralLoggerState,
	CentralLoggerClientState,
	LogLevels,
	RegisterLoggerClient,
	UnregisterLoggerClient,
	LogMessage,
} from "../../types";
import { State } from "./State";
import { defaultLogLevels } from "./State";
import { FEAWindow } from "../../../../FEA";
import JSZip from "jszip";
import { EventEmitter } from "events";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import clone from "lodash/clone";
import { saveAs } from "file-saver";
import { eachSeries as asyncForEachSeries } from "async";

const FILTER_DEBOUNCE_TIME = 200;
const SEARCH_DEBOUNCE_TIME = 0;
const MASTER_MAX_SIZE = 100 * 1000;
const MASTER_DEFAULT_SIZE = 10 * 1000;

export const showFocusBTF = (win: FEAWindow) => {
	win.restore();
	win.show();
	win.bringToFront(() => {
		win.focus();
	});
};

// LoggerStore is the single React data store supporting the central logger. All communication with clients through router also happens here.
// This store support the pure React model, but Flux not needed (i.e. there is no Dispatcher).
// All changes of store are communicated back to components using the ; on receiving an  event components then call back to this store to get the new data.
// Actions triggered by components aren't broken out in a separate class -- they are the "set" and "toggle" methods in the LoggerStore.
// Generally, all actions cause an  event to be sent (might a rare exception or two).

// There are lots of simularly named operations and functions in this store (due to the nature of the problem).  The easiest way to identify UI functionality is to start in
// the compponents, seeing what is controlled and map that behavior to the store operations.

type UIState = {
	console: LogLevels;
	dev: LogLevels;
	system: LogLevels;
	perf: LogLevels;
	clientChannel: string;
	showAdvancedViewFilters: boolean;
	wrapLog: boolean;
	showStackStraceInLog: boolean;
	filter: {
		logic: "OR" | "AND";
	};
	showTimeElapsedFromStartup: boolean;
	clientListVisible: boolean;
	colDefs: Record<string, any>;
};

export type LoggerClient = {
	name: string;
	viewId: number | null;
	centralLoggerNamePrefix: string;
	displayName: string;
};

export type SearchString = {
	str: string;
};

type MatchFunction = (logEntry: FormattedLogMessage, searchString: string) => boolean;

const matches: MatchFunction = (message: FormattedLogMessage, searchString: string) =>
	searchString.length > 0 && message.allLogArgs.toLowerCase().indexOf(searchString.toLowerCase()) !== -1;

const and = (searchStrings: SearchString[], message: FormattedLogMessage): boolean => {
	// Fail if none of the search strings are in the log
	for (let s = 0; s < searchStrings.length; s++) {
		let { str } = searchStrings[s];
		const excluding = str.charAt(0) === "-";
		str = excluding ? str : str.substr(1);

		const matchesFilter = !str || matches(message, str);
		// excluding means we've put a minus sign in front of the search, to indicate that we want to exclude the string from the results
		if (!matchesFilter && !excluding) return false;
		// if the filter matches and we're excluding, return false. Otherwise wait for other, non-exclusionary matches to break the loop.
		if (excluding && matchesFilter) return false;
	}
	return true;
};

const or = (searchStrings: SearchString[], message: FormattedLogMessage): boolean => {
	// Pass if any of the search strings are in the log, and not excluded by another search
	let matchFound = false;
	for (let s = 0; s < searchStrings.length; s++) {
		let { str } = searchStrings[s];
		const excluding = str.charAt(0) === "-";
		str = excluding ? str : str.substr(1);

		const matchesFilter = !str || matches(message, str);
		if (matchesFilter && excluding) return false;
		// if any search string matches, it's included in the result.
		if (matchesFilter) matchFound = true;
	}
	return matchFound;
};

let messageCounter = 0;
let firstMessageTime = Infinity;
let fileModeState = false;
let registeredClientNames: string[] = [];
let registeredClients: Record<string, LoggerClient> = {};
let masterLog: FormattedLogMessage[] = [];
let filteredLog: FormattedLogMessage[] = [];
const defaultShowClientState = true;
let windowWidth = 0; // min width to hold buttons, etc
let windowHeight = 0;
const defaultClientState: UIState = {
	console: defaultLogLevels,
	dev: defaultLogLevels,
	system: defaultLogLevels,
	perf: defaultLogLevels,
	clientChannel: "",
	showAdvancedViewFilters: true,
	wrapLog: true,
	showStackStraceInLog: true,
	filter: { logic: "OR" },
	showTimeElapsedFromStartup: false,
	clientListVisible: true,
	colDefs: {},
};

const disableClientState: UIState = {
	console: defaultLogLevels,
	dev: defaultLogLevels,
	system: defaultLogLevels,
	perf: defaultLogLevels,
	clientChannel: "",
	showAdvancedViewFilters: true,
	wrapLog: true,
	showStackStraceInLog: true,
	filter: { logic: "OR" },
	showTimeElapsedFromStartup: false,
	clientListVisible: true,
	colDefs: {},
};
const disableClientConsoleSetting: LogLevels = {
	Error: false,
	Warn: false,
	Info: false,
	Log: false,
	Debug: false,
	Verbose: false,
};
const disableClientSetting: LogLevels = {
	Error: false,
	Warn: false,
	Info: false,
	Log: false,
	Debug: false,
	Verbose: false,
	LocalOnly: false,
};
const SHOW_ON_ERROR_PREFERENCE_KEY = "finsemble.preferences.loggerService.showOnError";
const SHOW_ON_STARTUP_PREFERENCE_KEY = "finsemble.preferences.loggerService.showOnStartup";
const LARGE_LOG_SIZE_LINES = 50000;
const LINES_PER_LOG_FILE = 1000;

// utility to parse JSON and handle exception as an undefined
const jsonParse = (str: string): any => {
	try {
		return JSON.parse(str);
	} catch (e) {
		return undefined;
	}
};

// Sort files by filename
const zipToFileNames = (zip: JSZip) => {
	let outOfOrder = false;
	const filenames = Object.keys(zip.files).sort((name1, name2) => {
		const regexTest = /\/log[0-9]*.json$/;

		// If name1 or name2 are not log#.json files move them to the end of the array
		if (!regexTest.test(name1)) {
			return 1;
		} else if (!regexTest.test(name2)) {
			return -1;
		} else {
			// Strip filenames to get the index number. Expected filenames will be "*/log#.json". Strip off the '/log' and '.json'
			const fileIndex1 = Number(name1.substring(name1.search(regexTest) + 4, name1.length - 5));
			const fileIndex2 = Number(name2.substring(name2.search(regexTest) + 4, name2.length - 5));

			// If the stripped numbers fail to parse, warn the user that some of the logs might be out of order
			if (!Number.isNaN(fileIndex1) && !Number.isNaN(fileIndex2)) {
				return fileIndex1 - fileIndex2;
			} else {
				outOfOrder = true;
				return 0;
			}
		}
	});
	return { filenames, outOfOrder };
};

const processZip = async (zip: JSZip) =>
	new Promise<{
		outOfOrder: boolean;
		messages: FormattedLogMessage[];
		persistState: CentralLoggerState;
	}>((resolve) => {
		// Sort files by filename
		const { filenames, outOfOrder } = zipToFileNames(zip);

		// handle importing zip of log files
		const messages: FormattedLogMessage[] = [];

		let persistState: CentralLoggerState;

		/**
		 * Processes a list of imported client names, building up the leftnav (registeredClients)
		 * // import/export should be modified to save displayName, centralLoggerPrefix, etc
		 */
		const importClientNames = (importedRegisteredClientNames: string[]) => {
			registeredClientNames = registeredClientNames ?? importedRegisteredClientNames;
			registeredClients = {};
			registeredClientNames.forEach((name) => {
				registeredClients[name] = {
					name,
					viewId: 0,
					centralLoggerNamePrefix: "",
					displayName: name,
				};
			});
		};

		// read each file inside the zip and concatenate into one big array
		asyncForEachSeries(
			filenames,
			async (fileName) =>
				new Promise<void>(async (resolveLoad) => {
					const zipEntry = zip.files[fileName];
					const chunk = await zipEntry.async("blob");
					const fr = new FileReader();
					fr.onload = () => {
						const json = jsonParse(fr.result as string);
						const { length: bytes } = fr.result as string;
						console.log(`Processing logfile ${fileName} - ${bytes} bytes`);
						// handle the file with the special data
						if (json && fileName === "log_state.json") {
							const { registeredClientNames: importedRegisteredClientNames } = json;
							importClientNames(importedRegisteredClientNames);
							({ persistState } = json);
						} else if (json && json.partial_log) {
							const partialLogArray = json.partial_log;

							// sort logs by timestamp
							partialLogArray.sort((logA, logB) => logA.logTimestamp - logB.logTimestamp);

							// concatenate the log files. For performance reasons it was necessary to use push instead of concat
							// see: https://dev.to/uilicious/javascript-array-push-is-945x-faster-than-array-concat-1oki
							Array.prototype.push.apply(messages, partialLogArray);
						}
						resolveLoad();
					};
					fr.readAsText(chunk);
				}),
			() => {
				resolve({ outOfOrder, messages, persistState });
			}
		);
	});

class Store extends EventEmitter {
	// Used to detect ctrl clicks
	modifierKeys = {
		ctrlKey: false,
		altKey: false,
		shiftKey: false,
		metaKey: false,
	};

	windowVisibility: string = "visible";

	activeView: "logger" | "config" = "logger";

	Search: CtrlFSearch = new CtrlFSearch();

	showOnError: boolean = false;

	showOnStartup: boolean = false;

	activeRowIndex: number = 0;

	rowTimediff: number | null = null;

	initialized: boolean = false;

	shouldShowSearchBox: boolean = false;

	broadCastNewMessageUpdateThrottled: () => void = throttle(this.broadCastNewMessageUpdate.bind(this), 1000);

	persistState: State = new State();

	activeConfigSearchMatch: string = "";

	stringifiedManifest: string[] = [];

	currentMasterSize = MASTER_DEFAULT_SIZE;

	constructor() {
		super();
		this.setMaxListeners(100);
		this.bindCorrectContext();
		this.registerGlobalHotkey();
		this.setDefaults();
	}

	bindCorrectContext() {
		this.setFilter1 = debounce(this.setFilter1, FILTER_DEBOUNCE_TIME);
		this.setFilter2 = debounce(this.setFilter2, FILTER_DEBOUNCE_TIME);
		this.setFilter3 = debounce(this.setFilter3, FILTER_DEBOUNCE_TIME);
		this.setFilter4 = debounce(this.setFilter4, FILTER_DEBOUNCE_TIME);
		this.setLogSearchBoxString = this.setLogSearchBoxString.bind(this);
		this.setLogSearchBoxString = debounce(this.setLogSearchBoxString, SEARCH_DEBOUNCE_TIME);
		this.setWindowHidden = this.setWindowHidden.bind(this);
		this.setWindowVisible = this.setWindowVisible.bind(this);
		this.emitResizeTimeout = this.emitResizeTimeout.bind(this);
		this.setConfigVisible = this.setConfigVisible.bind(this);
		this.setLoggerVisible = this.setLoggerVisible.bind(this);
		this.clearData = this.clearData.bind(this);
		this.toggleShowOnError = this.toggleShowOnError.bind(this);
		this.toggleShowOnStartup = this.toggleShowOnStartup.bind(this);
		this.configureLoggerForDefaults = this.configureLoggerForDefaults.bind(this);
		this.configureLoggerForErrors = this.configureLoggerForErrors.bind(this);
		this.configureLoggerForFieldCapture = this.configureLoggerForFieldCapture.bind(this);
	}

	initialize() {
		windowWidth = window.innerWidth; // min width to hold buttons, etc
		windowHeight = window.innerHeight;
		this.addHotkeys();
		this.addWindowListeners();
	}

	addWindowListeners() {
		const finWindow = System.Window.getCurrent();
		finWindow.addEventListener("bounds-change-end", (event: any) => {
			this.processSizeChange(event.width, event.height);
		});

		finWindow.addEventListener("maximized", () => {
			this.processSizeChange(window.outerWidth, window.outerHeight);
		});

		finWindow.addEventListener("restored", () => {
			this.processSizeChange(window.outerWidth, window.outerHeight);
		});
		finWindow.addEventListener("shown", this.setWindowVisible);
		finWindow.addEventListener("restored", this.setWindowVisible);
		finWindow.addEventListener("hidden", this.setWindowHidden);
		finWindow.addEventListener("minimized", this.setWindowHidden);
	}

	/**
	 * Note: this method is also used by e2e tests. Do not change the signature without
	 * also changing the tests
	 */
	getRegisteredClientNames() {
		return registeredClientNames;
	}

	/**
	 * Note: this method is also used by e2e tests. Do not change the signature without
	 * also changing the tests
	 */
	getState(): CentralLoggerState {
		return this.persistState.data;
	}

	getRegisteredClients(): Record<string, LoggerClient> {
		return registeredClients;
	}

	getWindowWidth() {
		return windowWidth;
	}

	getWindowHeight() {
		return windowHeight;
	}

	/**
	 * This will look in the logger config to see if a global hotkey is registered to pull
	 * up the central logger. This is typically ctrl-~ but can be configured by developers
	 * as appropriate.
	 */
	async registerGlobalHotkey() {
		await SystemManagerClient.waitForStartup("configService");
		const { data: value } = await ConfigClient.getValue({
			field: "finsemble.servicesConfig.logger.hotkeyShowCentralLogger",
		});
		if (value) {
			await SystemManagerClient.waitForStartup("hotkeysService");
			HotkeyClient.addGlobalHotkey(value, () => {
				(window as any).showConsole();
			});
		}
	}

	addHotkeys() {
		const setModifiers = (e: KeyboardEvent) => {
			this.modifierKeys = {
				ctrlKey: e.ctrlKey,
				altKey: e.altKey,
				shiftKey: e.shiftKey,
				metaKey: e.metaKey,
			};
		};
		// When the window loses focus, all modifiers must be set to off
		window.addEventListener("blur", () => {
			this.modifierKeys = {
				ctrlKey: false,
				altKey: false,
				shiftKey: false,
				metaKey: false,
			};
		});
		// Keep track of whether any of the modifier keys have been pressed or released
		window.addEventListener("keydown", setModifiers);
		window.addEventListener("keyup", setModifiers);
		window.addEventListener("keydown", (e) => {
			if (e.ctrlKey && e.altKey) {
				switch (e.key) {
					case "c":
						this.toggleClientListVisible();
						break;
					case "d":
						this.configureLoggerForDefaults();
						break;
					case "e":
						this.configureLoggerForErrors();
						break;
					// s for support...
					case "s":
						this.configureLoggerForFieldCapture();
						break;
					case "x":
						this.clearData();
						break;
					default:
						break;
				}
			}
		});
	}

	getFileMode() {
		return fileModeState;
	}

	getFirstMessageTime() {
		return firstMessageTime;
	}

	// Log list things

	// rebuild the current log based on the current state of the store
	broadCastNewMessageUpdate() {
		if (this.windowVisibility !== "hidden") {
			// if  we modify the message, clone it.
			this.emit("loggerNewMessages");
		}
	}

	getMasterLog(): FormattedLogMessage[] {
		return masterLog;
	}

	/**
	 * Note: this method is also used by e2e tests. Do not change the signature without
	 * also changing the tests
	 */
	getFilteredLog(): FormattedLogMessage[] {
		return filteredLog;
	}

	getLogCounts() {
		return { master: masterLog.length, display: filteredLog.length };
	}

	maybeOpenClient({ windowName, viewId }: { windowName: string; viewId: number | null }) {
		if (viewId || windowName) {
			const win = System.Window.wrap(windowName);

			if (this.modifierKeys.altKey) {
				showFocusBTF(win);
			} else {
				System.showDeveloperTools(windowName, viewId);
			}
		}
	}

	getRowTimeDiff(): number | null {
		return this.rowTimediff;
	}

	setRowTimeDiff(diff: number | null) {
		this.rowTimediff = diff;
		this.emit("rowTimeDiffChange");
	}

	getClientListVisible(): boolean {
		return this.persistState.get("clientListVisible");
	}

	// List rendering functions
	triggerRowheightRecalculation() {
		this.emit("recalculateHeight");
	}

	triggerLogRerender() {
		this.emit("rerenderList");
	}

	toggleClientListVisible() {
		this.persistState.toggle("clientListVisible");
		this.emit("clientListVisibleChange");
	}

	// This is because we have to wait for dom elements to unmount so that the table can calculate its height. I hate it but it works.
	emitResizeTimeout() {
		setTimeout(() => {
			this.emit("WindowResize");
			this.emit("rerenderList");
		}, 400);
	}

	getLogState(): LogLevels {
		return this.persistState.get("logState");
	}

	getClientState(clientName: string): any {
		return this.persistState.get(`clientState[${clientName}]`);
	}

	getShowClientState(clientName: string) {
		return this.persistState.get(`showClientState[${clientName}]`);
	}

	getLogCategoriesAllowed() {
		const perf: boolean = this.persistState.get("perfModeState");
		const dev: boolean = this.persistState.get("devModeState");
		const system: boolean = this.persistState.get("systemModeState");
		return { perf, dev, system };
	}

	getAllShowClientState() {
		return this.persistState.get("showClientState");
	}

	getClientMode(): string {
		return this.persistState.get("currentCategory");
	}

	/**
	 * Note: this method is also used by e2e tests. Do not change the signature without
	 * also changing the tests
	 */
	clearData() {
		masterLog = [];
		filteredLog = [];
		this.emit("loggerNewMessages");
	}

	// Scrolling
	setTop() {
		this.emit("SetTop");
	}

	setBottom() {
		this.emit("SetBottom");
	}

	scrollToActiveRow() {
		this.emit("scrollToActiveRow");
	}

	setActiveRowIndex(ind: number) {
		this.activeRowIndex = ind;
		this.emit("activeRowChanged");
	}

	setActiveConfigSearchMatch(ind: string) {
		this.activeConfigSearchMatch = ind;
		this.emit("activeConfigSearchMatch");
	}

	getActiveConfigSearchMatch() {
		return this.activeConfigSearchMatch;
	}

	getActiveRowIndex() {
		return this.activeRowIndex;
	}

	getHideInactiveState(): boolean {
		return this.persistState.get("hideInactiveState");
	}

	toggleHideInactive() {
		this.persistState.toggle("hideInactiveState");
		this.emit("newHideInactiveState");
	}

	getCurrentCategory(): string {
		return this.persistState.get("currentCategory");
	}

	// LogLevel toggles
	toggleShowClient(clientName: string) {
		this.persistState.toggle(`showClientState[${clientName}]`);
		this.filterData();
		this.emit("newShowClientState");
		this.broadCastNewMessageUpdate();
	}

	// toggles visibility of all clients
	toggleShowAllClients() {
		let allTrue = true;
		for (const client in this.persistState.data.showClientState) {
			if (this.persistState.data.showClientState.hasOwnProperty(client)) {
				allTrue = allTrue && this.persistState.data.showClientState[client];
				if (!allTrue) {
					break;
				}
			}
		}
		const toggledState = !allTrue;
		for (const client in this.persistState.data.showClientState) {
			if (this.persistState.data.showClientState.hasOwnProperty(client)) {
				this.persistState.set(`showClientState[${client}]`, toggledState);
			}
		}
		this.filterData();
		this.emit("newShowClientState");
		this.broadCastNewMessageUpdateThrottled();
	}

	// CtrlF stuff
	getShowSearchBox() {
		return this.shouldShowSearchBox;
	}

	showSearchBox() {
		this.shouldShowSearchBox = true;
		this.emit("showSearchBoxChange");
	}

	hideSearchBox() {
		this.shouldShowSearchBox = false;
		this.setLogSearchBoxString("");
		this.emit("showSearchBoxChange");
	}

	getSearchBoxString(): string {
		return this.Search.searchString;
	}

	getSearchMatches() {
		return this.Search.iterator;
	}

	setLogSearchBoxString(str: string) {
		this.Search.performLogSearch(filteredLog, str);
		this.emit("searchBoxTextChange");
	}

	async setConfigSearchBoxString(str: string) {
		if (this.stringifiedManifest) {
			this.Search.performStringSearch(this.stringifiedManifest, str);
			this.emit("searchBoxTextChange");
		} else {
			await this.getFullConfig();
			this.Search.performStringSearch(this.stringifiedManifest, str);
			this.emit("searchBoxTextChange");
		}
	}

	// window visibility handlers
	setWindowVisible() {
		this.windowVisibility = "visible";
		this.broadCastNewMessageUpdate();
		this.emit("WindowVisibilityChange");
	}

	setWindowHidden() {
		this.windowVisibility = "hidden";
		this.emit("WindowVisibilityChange");
	}

	getWindowVisibility() {
		return this.windowVisibility;
	}

	setAllLogStateOn() {
		this.persistState.set("logState", {
			Error: true,
			Warn: true,
			Info: true,
			Log: true,
			Debug: true,
			Verbose: true,
		});
		this.emit("newDisplayState");
	}

	/**
	 * Note: this method is also used by e2e tests. Do not change the signature without
	 * also changing the tests
	 */
	setLogState(state: LogLevels) {
		this.persistState.set("logState", Object.assign(this.persistState.data.logState, state));
		this.emit("newDisplayState");
	}

	// Quickviews
	configureLoggerForDefaults() {
		this.persistState.set("initialClientStateDefault.Error", true);
		this.persistState.set("initialClientStateDefault.Warn", true);
		this.persistState.set("initialClientStateDefault.Log", true);
		this.persistState.set("initialClientStateDefault.Info", false);
		this.persistState.set("initialClientStateDefault.Debug", false);
		this.persistState.set("initialClientStateDefault.Verbose", false);
		// Sets the default client states to all of the clients.
		this.setAllLogStateOn();
		this.defaultShowClientAll();
		this.filterData();
		this.emit("newInitialClientStateDefault");
	}

	configureLoggerForErrors() {
		this.persistState.set("initialClientStateDefault.Error", true);
		this.emit("newInitialClientStateDefault");
		this.setLogState({
			Error: true,
			Warn: false,
			Info: false,
			Log: false,
			Debug: false,
			Verbose: false,
		});
		// Sets the default client states to all of the clients.
		this.defaultShowClientAll();
		this.filterData();
	}

	configureLoggerForFieldCapture() {
		this.persistState.set("initialClientStateDefault.Error", true);
		this.persistState.set("initialClientStateDefault.Warn", true);
		this.persistState.set("initialClientStateDefault.Log", true);
		this.persistState.set("initialClientStateDefault.Info", true);
		this.persistState.set("initialClientStateDefault.Debug", true);
		this.persistState.set("initialClientStateDefault.Verbose", false);

		this.emit("newInitialClientStateDefault");
		// Sets the default client states to all of the clients.
		this.defaultShowClientAll();
		this.persistState.persist();
		this.filterData();
		// Timeout to let the UI react and show colored buttons everywhere.
		setTimeout(() => {
			alert(
				`Logger is now configured for field capture. Please restart your application if needed, recreate problem, export the log, and send it to Finsemble Support with description of the problem. IMPORTANT: Once your log is exported, revert to the default logger settings; otherwise, you may see degraded performance due to the extra logging.`
			);
		}, 200);
	}

	configureLoggerForStartup() {
		this.persistState.set("initialClientStateDefault.Error", true);
		this.persistState.set("initialClientStateDefault.Warn", true);
		this.persistState.set("initialClientStateDefault.Log", true);
		this.persistState.set("initialClientStateDefault.Info", true);
		this.persistState.set("initialClientStateDefault.Debug", true);
		this.setFilter1("Service Online");
		this.emit("NewHighlightValues1");
		this.setFilter2("not online");
		this.emit("NewHighlightValues2");
		this.setFilter3("");
		this.emit("NewHighlightValues3");
		this.setFilter4("");
		this.emit("NewHighlightValues4");
		this.setFilterLogic("OR");
		this.emit("newInitialClientStateDefault");
		// Sets the default client states to all of the clients.
		this.defaultShowClientAll();
	}

	setFilteredLog(filteredData: any) {
		filteredLog = clone(filteredData);
	}

	/**
	 * Helper function to split the log array into smaller arrays
	 * Calling json.stringify can cause issues on large objects
	 * @param {} array
	 * @param {int} size
	 */
	chunk(array: any[], size: number) {
		const chunked_arr = [] as any[];
		let index = 0;
		while (index < array.length) {
			chunked_arr.push(array.slice(index, size + index));
			index = index + size;
		}
		return chunked_arr;
	}

	/**
	 * Export the logs to a zip file
	 */
	saveToFile() {
		// Handle the special data needed to reconstruct the log.
		const data = {
			registeredClientNames,
			registeredClients,
			persistState: this.persistState.data,
		};
		const json = JSON.stringify(data);
		const blob = new Blob([json], { type: "application/json" });

		// Create a zip and add the special data
		const zip = new JSZip();
		zip.file("log_state.json", blob);

		// Alert the user if the log has a large number of lines
		if (masterLog.length > LARGE_LOG_SIZE_LINES) {
			toast(
				"dev",
				"ALWAYS",
				"system",
				"Currently exporting a large log, this may take some time... (Check developer console for progress)"
			);
		}
		// Split the log into smaller arrays. This is needed because JSON.stringify runs out of memory with large data objects
		const chunked_array = this.chunk(masterLog, LINES_PER_LOG_FILE);
		this.emit("toggleOverlay", "Creating log export file. Please wait...");
		// For each split array, stringify it and create a zip file.
		for (let i = 0; i < chunked_array.length; i++) {
			const partial_log = chunked_array[i];
			console.log("adding to zip", i, partial_log);
			const str = JSON.stringify({ partial_log });
			const partial_blob = new Blob([str], { type: "application/json" });
			zip.file(`log${i}.json`, partial_blob);
		}

		// Finally save the zip file
		const log_time = new Date().toLocaleString();

		// setting compression(default: 'STORE' (no compression)) to 'DEFLATE' allows
		// us to set set the compression level [1 (best speed) - 9 (best compression )]
		zip
			.generateAsync({
				type: "blob",
				compression: "DEFLATE",
				compressionOptions: {
					level: 6,
				},
			})
			.then((result) => {
				this.emit("toggleOverlay");
				saveAs(result, `logs-${log_time}.zip`);
			});
	}

	setMasterLog(log: FormattedLogMessage[]) {
		masterLog = log;
	}

	setRegisteredClientNames(clientNames: string[]) {
		registeredClientNames = clientNames;
	}

	setRegisteredClients(clients: Record<string, LoggerClient>) {
		registeredClients = clients;
	}

	// enables fileMode, essentially disabling all external communication with clients in order to read log data from a file...a special case.
	fileMode(): void {
		fileModeState = true;

		const element = document.createElement("div");
		element.innerHTML = '<input type="file">';
		const fileInput = element.firstChild as HTMLInputElement;

		fileInput.addEventListener("change", async () => {
			const file = fileInput.files?.[0] as File;
			const isZip = !!file.name.match(/\.(zip)$/);
			if (!isZip) {
				const error = "File not supported. Import must be from a .zip file generated by the Central Logger";
				alert(error);
				console.error(error);
				return;
			}
			for (const client in this.persistState.data.showClientState) {
				if (
					this.persistState.data.showClientState.hasOwnProperty(client) &&
					(!this.persistState.data.hideInactiveState || this.persistState.data.showClientState[client])
				) {
					const clientState = { ...disableClientState }; // get a copy of the disabled state
					clientState.clientChannel = this.persistState.data.clientState[client]?.clientChannel; // don't overwrite the channel name
					RouterClient.transmit(clientState.clientChannel, clientState);
					console.debug("disabledClientState", clientState.clientChannel, clientState);
				}
			}

			// Load the zip file
			const zip = await JSZip.loadAsync(file);
			// Pop an alert if the zip contains a lot of files. This is a bit arbitrary as the length of time to import is based on the file size not the number of files
			// However we only have access to the number of files
			const numberOfFiles = Object.keys(zip.files).length;
			if (!("log_state.json" in zip.files)) {
				toast("dev", "ALWAYS", "system", `Not a valid central logger zip file. Perhaps it was double zipped?`);
				return;
			}
			this.emit("toggleOverlay", "Importing log file. Please wait...");
			if (numberOfFiles > 8) {
				toast(
					"dev",
					"ALWAYS",
					"system",
					`Currently importing a large log (${numberOfFiles} files). This may take some time... (Check developer console for progress)`
				);
			}

			const { outOfOrder, messages, persistState } = await processZip(zip);
			if (!messages.length) {
				toast("dev", "ALWAYS", "system", "Failure to import log - message list is empty!");
				return;
			} else if (outOfOrder) {
				toast("dev", "ALWAYS", "system", "There was a problem sorting file names, logs may appear out of order");
			} else {
				toast("dev", "ALWAYS", "system", "Log successfully imported!");
			}

			this.persistState.data = persistState;

			// when importing customer logs, always set currentMasterSize to the larger size.
			this.currentMasterSize = MASTER_MAX_SIZE;

			// finally turn off logging
			masterLog = messages;
			firstMessageTime = messages[0].logTimestamp;
			this.filterData();
			this.emit("newFirstMessageTime");
			this.emit("FileMode");
			this.emit("LoggerRegister");
			this.emit("newFilterHighlights");
			this.emit("newDisplayState");
			this.emit("LoggerUnregister");
			this.emit("newShowClientState");
			this.emit("toggleOverlay");
		});

		fileInput.click();
	}

	// For config only
	async getFullConfig(cb?: (config: string[]) => void) {
		const { data: manifest } = await ConfigClient.getManifest();
		const stringifiedManifest = JSON.stringify(manifest, null, 4);
		this.stringifiedManifest = stringifiedManifest.split(/\n/g);
		cb?.(this.stringifiedManifest);
	}

	// View handler
	getActiveView() {
		return this.activeView;
	}

	setLoggerVisible() {
		this.activeView = "logger";
		this.emit("viewChange");
	}

	setConfigVisible() {
		this.activeView = "config";
		this.emit("viewChange");
	}

	findIndexByTimestamp(logs: any[], timestamp: number) {
		for (var i = logs.length - 1; i > 0; i--) {
			if (logs[i].logTimestamp < timestamp) {
				break;
			}
		}
		return i;
	}

	// Preferences
	toggleShowOnStartup() {
		this.setShowOnStartup(!this.showOnStartup);
	}

	getShowOnStartup() {
		return this.showOnStartup;
	}

	setShowOnStartup(val: boolean, setPreference = true) {
		this.showOnStartup = val;
		this.emit("showOnStartup", this.showOnStartup);
		// we aren't going to set the preference on startup. any other time we will.
		if (setPreference) {
			ConfigClient.setPreference({
				field: SHOW_ON_STARTUP_PREFERENCE_KEY,
				value: this.showOnStartup,
			});
		}
	}

	toggleShowOnError() {
		this.setShowOnError(!this.showOnError);
	}

	getShowOnError() {
		return this.showOnError;
	}

	setShowOnError(val: boolean, setPreference = true) {
		this.showOnError = val;
		this.emit("showOnError", this.showOnError);
		// we aren't going to set the preference on startup. any other time we will.
		if (setPreference) {
			ConfigClient.setPreference({
				field: SHOW_ON_ERROR_PREFERENCE_KEY,
				value: this.showOnError,
			});
		}
	}

	// called whenever the Logger's window size is changed
	processSizeChange(width: number, height: number) {
		if (windowWidth !== width) {
			windowWidth = window.innerWidth;
			this.emit("WindowResize");
			this.emit("newShowClientState"); // don't know why but table size chance alone doesn't refresh table, so sent this too to trigger refresh
		}
		if (windowHeight !== height) {
			// the height isn't fully tracked because less important
			windowHeight = window.innerHeight;
			this.emit("WindowResize");
			this.emit("newShowClientState"); // don't know why but table size chance alone doesn't refresh table, so sent this too to trigger refresh
		}
	}

	// this sorts only the end of the table....when getting a new buffer of messages, only need to sort from the earliest timestamp forward;
	// the existing buffer (before new buffer is added) may have older timestamps, which is why must start from earliest timestamp in new messages
	sortNewLogMessages(fromTimestamp: number) {
		// compare function to sort messages by timestamp
		function compareTimestamps(a, b) {
			let result = 0;
			if (a.logTimestamp < b.logTimestamp) {
				result = -1;
			}
			if (a.logTimestamp > b.logTimestamp) {
				result = 1;
			}
			return result;
		}

		let indexToSortFrom = this.findIndexByTimestamp(masterLog, fromTimestamp);
		let newLogPortion = masterLog.splice(indexToSortFrom, masterLog.length);
		newLogPortion.sort(compareTimestamps);
		masterLog = masterLog.concat(newLogPortion);
		// When we splice masterLog, we return a new array, breaking the reference from filterLog to masterLog. If we are not currently filtering data, we have to reset the pointer in the else block.
		if (this.shouldFilterData()) {
			indexToSortFrom = this.findIndexByTimestamp(filteredLog, fromTimestamp);
			// If the last item in the list is the latest timestamp, no need to splice and force re-renders.
			if (indexToSortFrom !== filteredLog.length - 1) {
				newLogPortion = filteredLog.splice(indexToSortFrom, filteredLog.length);
				newLogPortion.sort(compareTimestamps);
				filteredLog = filteredLog.concat(newLogPortion);
			}
		} else {
			filteredLog = clone(masterLog);
		}
	}

	// Set the master log size, pruning data as needed.  The purpose is to keep the master log small except when a larger master is justified (e.g. when debug logger is enable somewhere).
	adjustCurrentMasterLogSize(newLogsPortionLength: number = 0) {
		const isHighLoggingActive = (logLevels, client): boolean =>
			logLevels.some((level) => client.system?.[level] === true) ||
			logLevels.some((level) => client.dev?.[level] === true);

		// if any of these log levels are set anywhere, then use larger size
		const highVolumeLoggingLevels = ["Info", "Debug", "Verbose"];

		const clients = this.persistState.get("clientState") ?? [];

		const isSetForHighVolumeLogging = Object.values(clients).some((client) =>
			isHighLoggingActive(highVolumeLoggingLevels, client)
		);

		// Note will increase currentMasterSize if needed but never decrease after that.
		// Automatically decreasing is doable, but then valuable log messages might be dropped when a developer turns off debug mode.
		if (isSetForHighVolumeLogging) {
			this.currentMasterSize = MASTER_MAX_SIZE;
		}

		// Always do this check -- new messages may have been added
		// updates logs size including size of the new portion of logs
		const newLength = masterLog.length + newLogsPortionLength;
		if (newLength > this.currentMasterSize) {
			// Limit by slicing the queue
			masterLog = masterLog.slice(newLength - this.currentMasterSize);
		}
	}

	// Adapter-interface handlers
	onNewMessage(messages: LogMessage[]) {
		const filteredLogLength = filteredLog.length;
		if (fileModeState) return;

		// typically not in filemode, so go through normal handling of log messages, but ignore messages in file mode
		let earliestTimestamp = Infinity;
		let newMessage: FormattedLogMessage;

		// Adjust the master log size -- this operation should be cheap enough to run here.
		// Should be adjusted with the size of the new logs
		this.adjustCurrentMasterLogSize(messages.length);

		for (let i = 0; i < messages.length; i++) {
			if (this.showOnError && messages[i].logType === "Error") {
				(window as any).showConsole();
			}

			newMessage = formatLogMessage(messages[i]);
			if (messageCounter++ < 10000 && newMessage.logTimestamp < firstMessageTime) {
				// look for the lowest time value in early messages to set base time
				firstMessageTime = newMessage.logTimestamp;
				this.emit("newFirstMessageTime");
			}
			if (newMessage.logTimestamp < earliestTimestamp) {
				earliestTimestamp = newMessage.logTimestamp;
			}
			newMessage.timeElapsedFromStartup = Math.round(newMessage.logTimestamp - firstMessageTime);
			masterLog.push(newMessage);
			const pushToFilteredLog = this.shouldFilterData() && this.filterData(newMessage).length;
			if (pushToFilteredLog) {
				filteredLog.push(newMessage);
			}
		}

		this.sortNewLogMessages(earliestTimestamp);

		// Updates the filtered Log size to prevent memory leak
		if (filteredLog.length > this.currentMasterSize) {
			filteredLog = filteredLog.slice(filteredLog.length - this.currentMasterSize);
			this.broadCastNewMessageUpdateThrottled();
		}
		// broadcast if the length was changed or the length is maximum
		else if (filteredLogLength !== filteredLog.length || filteredLogLength === this.currentMasterSize) {
			this.broadCastNewMessageUpdateThrottled();
		}
	}

	onClientRegistered(data: RegisterLoggerClient): CentralLoggerClientState {
		const { clientName: tempName, displayName, viewId, centralLoggerNamePrefix } = data;
		const clientName = viewId ? `${tempName}: view-${viewId}` : tempName;

		if (!fileModeState) {
			// typically not in filemode, so go through normal registration
			console.debug("LoggerStore.registerClient", clientName, data);

			if (!registeredClientNames.includes(clientName)) {
				registeredClientNames.push(clientName);
				registeredClients[clientName] = {
					name: tempName,
					viewId,
					centralLoggerNamePrefix,
					displayName,
				};
			}

			if (!(clientName in this.persistState.data.showClientState)) {
				this.persistState.set(`showClientState[${clientName}]`, defaultShowClientState);
			}

			if (!(clientName in this.persistState.data.clientState)) {
				// the data will from from persistState.data. but if not in persistState.data. add it using default values
				const newClientState = Utils.clone(defaultClientState);
				this.persistState.set(`clientState[${clientName}]`, newClientState);
				this.persistState.set(`clientState[${clientName}].clientChannel`, data.clientChannel);
				this.persistState.set(`clientState[${clientName}].windowName`, data.windowName);
			}

			const debugState = this.persistState.data.clientState[clientName];
			this.filterData();
			this.emit("LoggerRegister");
			// added this  because the react elements weren't getting updated with the proper state as clients registered. I suspect the virtualDOM was doing a diff and getting it wrong..
			this.emit("newClientState");
			this.emit("newShowClientState");
			return debugState;
		} else {
			// ignore registration if in filemode -- send back disabled state
			console.debug("Registration in FileMode");
			const newClientState = { ...disableClientState };
			newClientState.clientChannel = data.clientChannel;
			return newClientState;
		}
	}

	unRegisterClient(data: UnregisterLoggerClient) {
		const clientName = data.viewId ? `${data.clientName}: view-${data.viewId}` : data.clientName;
		const { deleteFromPersistence } = data;

		for (let i = 0; i < registeredClientNames.length; i++) {
			if (registeredClientNames[i] === clientName) {
				registeredClientNames.splice(i, 1);
				i--;

				delete registeredClients[clientName];
			}
		}

		if (deleteFromPersistence) {
			delete this.persistState.data.clientState[clientName];
			delete this.persistState.data.showClientState[clientName];
			this.persistState.persist();
		}
		this.emit("LoggerUnregister");
	}

	setDefaults() {
		defaultClientState.console = this.persistState.data.initialClientStateDefault as LogLevels;
		defaultClientState.dev = this.persistState.data.initialClientStateDefault as LogLevels;
		defaultClientState.system = this.persistState.data.initialClientStateDefault as LogLevels;
		defaultClientState.perf = this.persistState.data.initialClientStateDefault as LogLevels;
		defaultClientState.clientChannel = "";
		defaultClientState.showAdvancedViewFilters = this.persistState.data.showAdvancedViewFilters;
		defaultClientState.wrapLog = this.persistState.data.wrapLog;
		defaultClientState.showStackStraceInLog = this.persistState.data.showStackStraceInLog;
		defaultClientState.filter = this.persistState.data.filter;
		defaultClientState.showTimeElapsedFromStartup = this.persistState.data.showTimeElapsedFromStartup;
		defaultClientState.clientListVisible = this.persistState.data.clientListVisible;
		defaultClientState.colDefs = this.persistState.data.colDefs;
		disableClientState.console = disableClientConsoleSetting;
		disableClientState.dev = disableClientSetting;
		disableClientState.system = disableClientSetting;
		disableClientState.perf = disableClientSetting;
		disableClientState.clientChannel = "";
	}

	// Remove from persisted state all stale clients.  A client is consider stale if during normal operation it is in persisted state but not registered.
	// Stale clients occur because some clients (e.g. window-open-finsemble clients) never unregister during a shutdown, accumulating then in persisted state for all future runs.
	removeUnregisteredClientsFromPersistState() {
		const persistedClientNames = Object.keys(this.persistState.data.clientState);

		// first remove from store the clients that aren't registered
		persistedClientNames.forEach((persistedName) => {
			if (!registeredClientNames.includes(persistedName)) {
				delete this.persistState.data.clientState[persistedName];
				delete this.persistState.data.showClientState[persistedName];
			}
		});

		// then persist the store state to local storage
		this.persistState.persist();
	}

	/***** FILTERING METHODS *****/

	setFilter1(searchString) {
		this.persistState.set("highlightString[0]", {
			str: searchString.toString(),
		});
		if (this.persistState.data.filterHighlights) {
			this.filterData();
		}
		this.emit("NewHighlightValues1");
	}

	setFilter2(searchString) {
		this.persistState.set("highlightString[1]", {
			str: searchString.toString(),
		});
		if (this.persistState.data.filterHighlights) {
			this.filterData();
		}
		this.emit("NewHighlightValues2");
	}

	setFilter3(searchString) {
		this.persistState.set("highlightString[2]", {
			str: searchString.toString(),
		});
		if (this.persistState.data.filterHighlights) {
			this.filterData();
		}
		this.emit("NewHighlightValues3");
	}

	setFilter4(searchString) {
		this.persistState.set("highlightString[3]", {
			str: searchString.toString(),
		});
		if (this.persistState.data.filterHighlights) {
			this.filterData();
		}
		this.emit("NewHighlightValues4");
	}

	getFilterStrings(params = { removeEmpty: false }): SearchString[] {
		let strings = this.persistState.get("highlightString");
		if (!strings) strings = [];
		strings = strings.map((filter) => {
			if (typeof filter === "string") filter = { str: filter };
			return filter;
		});
		if (params.removeEmpty) {
			return strings.filter((filter) => {
				const { str } = filter;
				return str !== "";
			});
		}
		return strings;
	}

	// Toggles  all of the log-levels
	toggleDisplayAll() {
		const { logState } = this.persistState.data;
		// if any not true, then set them all to true; otherwise make them all false
		if (logState.Error && logState.Warn && logState.Info && logState.Log && logState.Debug && logState.Verbose) {
			this.persistState.set("logState", {
				Error: false,
				Warn: false,
				Info: false,
				Log: false,
				Debug: false,
				Verbose: false,
			});
		} else {
			this.persistState.set("logState", {
				Error: true,
				Warn: true,
				Info: true,
				Log: true,
				Debug: true,
				Verbose: true,
			});
		}
		this.filterData();
		this.emit("newDisplayState");
	}

	toggleDisplayErrors() {
		this.persistState.toggle("logState.Error");
		this.filterData();
		this.emit("newDisplayState");
	}

	toggleDisplayWarnings() {
		this.persistState.toggle("logState.Warn");
		this.filterData();
		this.emit("newDisplayState");
	}

	toggleDisplayInfo() {
		this.persistState.toggle("logState.Info");
		this.filterData();
		this.emit("newDisplayState");
	}

	toggleDisplayLogs() {
		this.persistState.toggle("logState.Log");
		this.filterData();
		this.emit("newDisplayState");
	}

	toggleDisplayDebug() {
		this.persistState.toggle("logState.Debug");
		this.filterData();
		this.emit("newDisplayState");
	}

	toggleDisplayVerbose() {
		this.persistState.toggle("logState.Verbose");
		this.filterData();
		this.emit("newDisplayState");
	}

	getDevModeState(): boolean {
		return this.persistState.get("devModeState");
	}

	getSystemModeState(): boolean {
		return this.persistState.get("systemModeState");
	}

	getPerfModeState(): boolean {
		return this.persistState.get("perfModeState");
	}

	devModeToggle() {
		this.persistState.toggle("devModeState");
		this.filterData();
		this.emit("ModeStateChange");
	}

	systemModeToggle() {
		this.persistState.toggle("systemModeState");
		this.filterData();

		this.emit("ModeStateChange");
	}

	perfModeToggle() {
		this.persistState.toggle("perfModeState");
		this.filterData();
		this.emit("ModeStateChange");
	}

	devMode() {
		this.persistState.set("currentCategory", "dev");
		this.emit("ChangeClientMode");
		this.emit("newClientState");
	}

	systemMode() {
		this.persistState.set("currentCategory", "system");
		this.emit("ChangeClientMode");
		this.emit("newClientState");
	}

	perfMode() {
		this.persistState.set("currentCategory", "perf");
		this.emit("ChangeClientMode");
		this.emit("newClientState");
	}

	// Filtering
	getFilterLogic(): string {
		return this.persistState.get("filter.logic");
	}

	setFilterLogic(logic: string) {
		this.persistState.set("filter.logic", logic);
		this.emit("filterLogicChange");
		this.filterData();
	}

	shouldFilterData() {
		const searchStrings = this.getFilterStrings({ removeEmpty: true });
		const allFiltersEmpty = searchStrings.length === 0;
		// if ever log level is true, return true. This is almost never true.
		const logCategoriesAllowed = this.getLogCategoriesAllowed();

		const logLevelsAllowed = this.getLogState();
		const allClientVisibility = this.getAllShowClientState();
		const allLogLevelsAllowed = Object.keys(logLevelsAllowed).every((level) => logLevelsAllowed[level]);
		const allLogCategoriesAllowed = Object.keys(logCategoriesAllowed).every(
			(category) => logCategoriesAllowed[category]
		);
		const allClientsAreVisible = Object.keys(allClientVisibility).every((client) => allClientVisibility[client]);
		// If everything is allowed, we should not filter data. If any are false (that is, some filter is enabled), we should filter data.
		return !(allLogCategoriesAllowed && allClientsAreVisible && allFiltersEmpty && allLogLevelsAllowed);
	}

	shouldNotFilterData() {
		return !this.shouldFilterData();
	}

	/**
	 * Main file
	 * @param {LogMessage} logMessage
	 */
	filterData(logMessage?: FormattedLogMessage): FormattedLogMessage[] {
		const filterAll = typeof logMessage === "undefined";
		const unfilteredData = typeof logMessage === "undefined" ? masterLog : [logMessage];
		const searchStrings = this.getFilterStrings({ removeEmpty: true });
		const isFilterInEffect = searchStrings.length !== 0;
		const allClientVisibility = this.getAllShowClientState();
		const allowedLogCategories = this.getLogCategoriesAllowed();
		const allowedLogLevels = this.getLogState();
		const shouldNotFilterData = this.shouldNotFilterData();
		const allClientsAreVisible = Object.keys(allClientVisibility).every((client) => allClientVisibility[client]);
		if (shouldNotFilterData) {
			this.setFilteredLog(masterLog);
			if (filterAll) {
				this.broadCastNewMessageUpdate();
			}
			return filteredLog;
		}

		const filterLogic = this.getFilterLogic();

		const filteredData = unfilteredData.filter((message) => {
			if (!allClientsAreVisible && !allClientVisibility[message.logClientName]) return false;
			const isLogLevelAllowed: boolean = allowedLogLevels[message.logType];
			const isLogCategoryAllowed: boolean = allowedLogCategories[message.category];

			// If we've excluded the level or category then don't display the message
			if (!isLogLevelAllowed || !isLogCategoryAllowed) return false;
			// If there are no filters in effect, then display the message
			if (!isFilterInEffect) return true;

			// If filters are in effect then use filter logic to determine whether to display the message
			if (filterLogic === "AND") return and(searchStrings, message);
			return or(searchStrings, message);
		});

		if (filterAll) {
			this.setFilteredLog(filteredData);
			this.broadCastNewMessageUpdate();
		}

		return filteredData;
	}

	showClientAllCategory(currentCategory: string) {
		for (const client in this.persistState.data.showClientState) {
			if (
				this.persistState.data.showClientState.hasOwnProperty(client) &&
				(!this.persistState.data.hideInactiveState || this.persistState.data.showClientState[client])
			) {
				const clientState = this.persistState.data.clientState[client];
				// Copy the default category state so that we don't accidentally create a back-reference
				clientState[currentCategory] = { ...this.persistState.data.initialClientStateDefault };
				RouterClient.transmit(clientState.clientChannel, clientState);
			}
		}
		this.emit("newClientState");
	}

	defaultShowClientAll() {
		this.showClientAllCategory("console");
		this.showClientAllCategory("dev");
		this.showClientAllCategory("system");
		this.showClientAllCategory("perf");
		this.emit("newClientState");
	}

	toggleClientStateAllCategories(clientName: string, type: string) {
		this.persistState.toggle(`clientState[${clientName}].dev[${type}]`);
		this.persistState.toggle(`clientState[${clientName}].system[${type}]`);
		this.persistState.toggle(`clientState[${clientName}].perf[${type}]`);
	}

	toggleClientErrors(clientName: string) {
		const clientState = this.persistState.data.clientState[clientName];
		this.toggleClientStateAllCategories(clientName, "Error");
		RouterClient.transmit(clientState.clientChannel, clientState);
		this.emit("newClientState");
	}

	toggleClientWarnings(clientName: string) {
		const clientState = this.persistState.data.clientState[clientName];
		this.toggleClientStateAllCategories(clientName, "Warn");
		RouterClient.transmit(clientState.clientChannel, clientState);
		this.emit("newClientState");
	}

	toggleClientInfo(clientName: string) {
		const clientState = this.persistState.data.clientState[clientName];
		this.toggleClientStateAllCategories(clientName, "Info");
		RouterClient.transmit(clientState.clientChannel, clientState);
		this.emit("newClientState");
	}

	toggleClientLogs(clientName: string) {
		const clientState = this.persistState.data.clientState[clientName];
		this.toggleClientStateAllCategories(clientName, "Log");
		RouterClient.transmit(clientState.clientChannel, clientState);
		this.emit("newClientState");
	}

	toggleClientDebug(clientName: string) {
		const clientState = this.persistState.data.clientState[clientName];
		this.toggleClientStateAllCategories(clientName, "Debug");
		RouterClient.transmit(clientState.clientChannel, clientState);
		this.emit("newClientState");
	}

	toggleClientVerbose(clientName: string) {
		const clientState = this.persistState.data.clientState[clientName];
		this.toggleClientStateAllCategories(clientName, "Verbose");
		RouterClient.transmit(clientState.clientChannel, clientState);
		this.emit("newClientState");
	}

	toggleClientLocalOnly(clientName: string) {
		const clientState = this.persistState.data.clientState[clientName];
		this.toggleClientStateAllCategories(clientName, "LocalOnly");
		RouterClient.transmit(clientState.clientChannel, clientState);
		this.emit("newClientState");
	}

	toggleInitialClientErrorsDefault() {
		this.persistState.toggle("initialClientStateDefault.Error");
		this.emit("newInitialClientStateDefault");
		this.defaultShowClientAll();
	}

	toggleInitialClientWarningsDefault() {
		this.persistState.toggle("initialClientStateDefault.Warn");
		this.emit("newInitialClientStateDefault");
		this.defaultShowClientAll();
	}

	toggleInitialClientInfoDefault() {
		this.persistState.toggle("initialClientStateDefault.Info");
		this.emit("newInitialClientStateDefault");
		this.defaultShowClientAll();
	}

	toggleInitialClientLogsDefault() {
		this.persistState.toggle("initialClientStateDefault.Log");
		this.emit("newInitialClientStateDefault");
		this.defaultShowClientAll();
	}

	toggleInitialClientDebugDefault() {
		this.persistState.toggle("initialClientStateDefault.Debug");
		this.emit("newInitialClientStateDefault");
		this.defaultShowClientAll();
	}

	toggleInitialClientVerboseDefault() {
		this.persistState.toggle("initialClientStateDefault.Verbose");
		this.emit("newInitialClientStateDefault");
		this.defaultShowClientAll();
	}

	toggleInitialClientLocalOnlyDefault() {
		this.persistState.toggle("initialClientStateDefault.LocalOnly");
		this.emit("newInitialClientStateDefault");
		this.defaultShowClientAll();
	}

	getInitialClientStateDefault() {
		return this.persistState.get("initialClientStateDefault");
	}
}
const loggerStore = new Store();

export default loggerStore;
